import { afterNextRender, Component, DestroyRef, DOCUMENT, effect, Inject, inject, input, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { USERINFO } from '../../models/service';
import { LocalStorageService } from '../../services/local-storage.service';
import { environment } from '../../environment';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../services/api.service';
import { IProjectBasic, PROJECT_API_RESPONSE } from '../../models/Project.Model';
import { APP_CONSTANTS } from '../../app.constants';
import {takeUntilDestroyed as takeUntilDestroyedOp } from '@angular/core/rxjs-interop';


export interface SidebarMenu {
  title: string;
  icon: string;
  route?: string;
  groupName?: string;
  collapseId?: string;
  children?: SidebarMenu[];
}

@Component({
  selector: 'app-header',
  imports: [RouterLink, RouterLinkActive, CommonModule, TranslatePipe],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header {
  userSelectedLanguage = signal<string>(environment.AppDefaultLanguageCode);
  isLoggedIn = input.required<boolean>();
  userProfile = input.required<USERINFO>();
  translate = inject(TranslateService);
  olympiadMenu = signal<IProjectBasic[]>([]);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  EntityTypes:any = APP_CONSTANTS.ENTITY_TYPE_KEY as any;
  currentLang = signal('en');
  groupName : any = "";
  groupUrl : string = "";

  constructor(public localStorageInstance: LocalStorageService, private apiService: ApiService, @Inject(DOCUMENT) private document: Document) {
    afterNextRender(() => {
      this.prepareDetail();
       this.router.events.pipe(takeUntilDestroyedOp(this.destroyRef)).subscribe((event) => {
        if (event && event instanceof NavigationEnd) {
          this.onRouterLinkActive(event);
        } 
      });
      this.onRouterLinkActive(this.router);
    })    
    effect(() => {
      this.document.documentElement.lang = this.currentLang();
    });    
  }
  prepareDetail() {
    this.localStorageInstance.get(this.localStorageInstance.userLangaugeCodeKey).subscribe({
      next: (languageCode: any) => {
        let userSelectedLangaugeCode = languageCode ? (languageCode + '').trim().toString().toUpperCase() : environment.AppDefaultLanguageCode;
        this.onChangeLanguage(userSelectedLangaugeCode);
      },
      error: () => {
        this.onChangeLanguage(environment.AppDefaultLanguageCode);
      }
    }); 
  }

  getOlympiadList() {
    this.apiService.maskLoader(true);
    this.apiService.requestGet('mst/project-list').subscribe({
      next: (response: PROJECT_API_RESPONSE | any) => {
        this.olympiadMenu.set(response.dataobject);
        this.apiService.maskLoader(false);
      },
      error: (error) => {
        this.olympiadMenu.set([])
        this.apiService.maskLoader(false);
      }
    })
  }

  onChangeLanguage(language: string) {
    let selectedLanguageCode = (language || 'EN').toUpperCase();
    if (!environment.AppLanguageCodes.includes(selectedLanguageCode)) {
      selectedLanguageCode = environment.AppDefaultLanguageCode;
    }
    this.userSelectedLanguage.set(selectedLanguageCode);
    this.localStorageInstance.set(this.localStorageInstance.userLangaugeCodeKey, selectedLanguageCode).subscribe();
    this.translate.use(selectedLanguageCode.toLowerCase());
    this.currentLang.set(selectedLanguageCode.toLowerCase().toString());
    this.getOlympiadList();
  }

  logout() {
    this.localStorageInstance.clearAll().subscribe();
    window.location.reload();
  }

  instituteMenus: SidebarMenu[] = [
  {
    title: 'Dashboard',
    icon: 'bi bi-grid',
    route: '/sh/dashboard'
  },
  {
    title: 'My Profile',
    icon: 'bi bi-person',
    route: '/sh/profile'
  },
  {
    title: 'Student Registrations',
    icon: 'bi bi-person-plus',
    groupName: 'Student-section',
    collapseId: 'Student-collapse',
    children: [
      {
        title: 'Admission summary',
        icon: 'bi bi-record-fill',
        route: '/sh/student-summary'
      },
      {
        title: 'Student List',
        icon: 'bi bi-record-fill',
        route: '/sh/student-list'
      },
      {
        title: 'Student Bulk Registration',
        icon: 'bi bi-record-fill',
        route: '/sh/bulk-upload-list'
      }
    ]
  },    
  {
    title: 'Payments',
    icon: 'bi bi-credit-card',
    groupName: 'payment-section',
    collapseId: 'Payments-collapse',
    children: [
      {
        title: 'Fee Payment',
        icon: 'bi bi-record-fill',
        route: '/sh/fee-payment'
      },
      {
        title: 'Bulk Fee Payment',
        icon: 'bi bi-record-fill',
        route: '/sh/bulk-fee-payment'
      },
      {
        title: 'Payment history',
        icon: 'bi bi-record-fill',
        route: '/sh/payment-history'
      }
    ]
  },
  {
    title: 'Exam & Result',
    icon: 'bi bi-file-text',
    groupName: 'exam-section',
    collapseId: 'Exams-collapse',
    children: [
      // {
      //   title: 'Exam Instructions',
      //   icon: 'bi bi-record-fill',
      //   route: '/sm/exam-instructions'
      // },
      // {
      //   title: 'Exam Slot',
      //   icon: 'bi bi-record-fill',
      //   route: '/sm/book-exam-slot'
      // }
    ]
  },    
  {
    title: 'Support / Help',
    icon: 'bi bi-headset',
    route: '/sm/support'
  }
  ];

  studentMenus: SidebarMenu[] = [
  {
    title: 'Dashboard',
    icon: 'bi bi-grid',
    route: '/sm/dashboard'
  },
  {
    title: 'My Profile',
    icon: 'bi bi-person',
    route: '/sm/profile'
  },
  {
    title: 'Available Olympiads',
    icon: 'bi bi-award',
    route: '/sm/olympiads'
  },
  {
    title: 'My Programme',
    icon: 'bi bi-journal-bookmark',
    route: '/sm/my-programme'
  },
  {
    title: 'Payments',
    icon: 'bi bi-credit-card',
    groupName: 'payment-section',
    collapseId: 'Payments-collapse',
    children: [
      {
        title: 'Make Payment',
        icon: 'bi bi-record-fill',
        route: '/sm/make-payment'
      },
      {
        title: 'Payment history',
        icon: 'bi bi-record-fill',
        route: '/sm/payment-history'
      }
    ]
  },
  // {
  //   title: 'Exams',
  //   icon: 'bi bi-file-text',
  //   groupName: 'exam-section',
  //   collapseId: 'Exams-collapse',
  //   children: [
  //     {
  //       title: 'Exam Instructions',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/exam-instructions'
  //     },
  //     {
  //       title: 'Exam Slot',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/book-exam-slot'
  //     }
  //   ]
  // },
  // {
  //   title: 'Result',
  //   icon: 'bi bi-file-text',
  //   groupName: 'result-section',
  //   collapseId: 'Results-collapse',
  //   children: [
  //     {
  //       title: 'View Result',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/comming-soon'
  //     },
  //     {
  //       title: 'Download Certificate',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/comming-soon'
  //     }
  //   ]
  // },
  // {
  //   title: 'Study Material',
  //   icon: 'bi bi-book',
  //   groupName: 'study-material-section',
  //   collapseId: 'Study-collapse',
  //   children: [
  //     {
  //       title: 'Syllabus',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/olympiads'
  //     },
  //     {
  //       title: 'Sample Papers',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/olympiads'
  //     },
  //     {
  //       title: 'Practice Book',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/olympiads'
  //     }
  //   ]
  // },
  // {
  //   title: 'Mock Test',
  //   icon: 'bi bi-pencil-square',
  //   groupName: 'mock-test-section',
  //   collapseId: 'Mock-collapse',
  //   children: [
  //     {
  //       title: 'Take Mock Test',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/comming-soon'
  //     },
  //     {
  //       title: 'Test History',
  //       icon: 'bi bi-record-fill',
  //       route: '/sm/comming-soon'
  //     }
  //   ]
  // },
  {
    title: 'Support / Help',
    icon: 'bi bi-headset',
    route: '/sm/support'
  }
  ];


  onRouterLinkActive(event:any){
    const urlSplit =[...new Set((event.url || "").replace(/[0-9]/g, '').split("/").map((entry:any) => entry.trim()).filter(Boolean))];
    this.groupName = (urlSplit[0] || "");
    this.groupUrl = "/"+urlSplit.join("/");   
    if(urlSplit.includes('sm') && (urlSplit.includes('student-course-detail') || urlSplit.includes('init-payment-order'))){
      this.groupUrl = "/sm/my-programme";
    }
    if(urlSplit.includes('sm') && (urlSplit.includes('make-payment') || urlSplit.includes('payment-history')) ){
      this.groupName = "payment-section";
    }
    if(urlSplit.includes('sm') && (urlSplit.includes('exam-instructions') || urlSplit.includes('book-exam-slot')) ){
      this.groupName = "exam-section";
    }
    if(urlSplit.includes('sm') && (urlSplit.includes('result') || urlSplit.includes('certificate')) ){
      this.groupName = "result-section";
    }
    if(urlSplit.includes('sm') && (urlSplit.includes('syllabus') || urlSplit.includes('sample-papers')) || urlSplit.includes('practice-book') ){
      this.groupName = "study-material-section";
    }
    if(urlSplit.includes('sm') && (urlSplit.includes('mock-test') || urlSplit.includes('mock-test-history'))){
      this.groupName = "mock-test-section";
    }



    //Institute
     if(urlSplit.includes('bulk-upload-list') || urlSplit.includes('edit-student')  || urlSplit.includes('student-course-detail') || urlSplit.includes('student-summary') || urlSplit.includes('student-list') || urlSplit.includes('register-student')){
      this.groupName = "student-section";
    }
    if(urlSplit.includes('sh') && (urlSplit.includes('student-course-detail') || urlSplit.includes('edit-student') || urlSplit.includes('init-payment-order'))){
      this.groupUrl = "/sh/student-list";
    }
    
    if(urlSplit.includes('cms') && urlSplit.includes('edit')){
      this.groupUrl = "/cms/list";
    }
    if(urlSplit.includes('team-member-form')){
      this.groupUrl = "/team-member-form";
    }


    if(urlSplit.includes('bulk-fee-payment') || urlSplit.includes('payment-history') || urlSplit.includes('fee-payment') || urlSplit.includes('init-payment-order') || urlSplit.includes('payment-status')){
      this.groupName = "payment-section";
    }

    //console.log(urlSplit,'this.groupName', this.groupName, 'this.groupUrl', this.groupUrl)
  }
}
